import React from "react";
import { useWebSocket, ReadyState } from "@/lib/cjs-interop";
import {
  type BusinessReportOutcomeEvent,
  type BusinessReportOutcomeEvidenceEvent,
  type BusinessReportOutcomeStatusUpdateEvent,
} from "./types";
import { PromptResult } from "./businessReportOutcomeContent";
import { convertToEvidence } from "./helpers";
import { BusinessReportOutcomeDetails } from "@/modules/snapshots/businessReports.types";
import { Evidence } from "@/types/evidences";

interface Props {
  businessReportOutcome: BusinessReportOutcomeDetails;
  setIsComplete: (isComplete: boolean) => void;
  setPromptsResult: React.Dispatch<React.SetStateAction<PromptResult[]>>;
  setEvidences: React.Dispatch<React.SetStateAction<Evidence[]>>;
  refetch: () => void;
}

export const BusinessReportOutcomeWebsocket: React.FC<Props> = ({
  businessReportOutcome,
  setIsComplete,
  setEvidences,
  setPromptsResult,
  refetch,
}) => {
  const { lastJsonMessage, readyState, sendJsonMessage } = useWebSocket<
    BusinessReportOutcomeEvent | BusinessReportOutcomeEvidenceEvent | BusinessReportOutcomeStatusUpdateEvent
  >("/pf-mainweb/business-reports/ws", {
    shouldReconnect: event => event?.code !== 1000,
    reconnectInterval: 3000,
    skipAssert: true,
  });

  React.useEffect(() => {
    if (lastJsonMessage !== null) {
      if (lastJsonMessage?.event === "business_report_outcome_status_update") {
        refetch();
        setIsComplete(true);
      } else if (lastJsonMessage?.event === "business_report_outcome_result") {
        if (businessReportOutcome.status !== "COMPLETED") {
          const msg = lastJsonMessage;

          if (!msg.context) {
            return;
          }

          setPromptsResult(prev =>
            prev.map(row => ({
              ...row,
              content:
                row.business_report_outcome_result_id === msg.business_report_outcome_result_id
                  ? msg.context
                  : row.content,
            })),
          );
        }
      } else {
        const msg = lastJsonMessage as BusinessReportOutcomeEvidenceEvent;

        setEvidences(prev => [...prev, convertToEvidence(msg)]);
      }
    }
  }, [lastJsonMessage, setPromptsResult, setIsComplete, businessReportOutcome.status, setEvidences, refetch]);

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendJsonMessage({ query_id: businessReportOutcome.id });
    }
  }, [readyState, sendJsonMessage, businessReportOutcome.id]);

  return null;
};
